import {
  DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
} from '@/shared/config'
import { type Query, createQuery } from '@farfetched/core'
import {
  type EventCallable,
  type Store,
  type StoreWritable,
  combine,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { debounce } from 'patronum'

import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../lib/query'

type BaseQueryModelParams = {
  /**
   * @description Строка поиска
   * @type string | undefined
   */
  SearchQuery?: string
  /**
   * @description Пагинация - сколько необходимо получить элементов
   * @type integer | undefined, int32
   */
  Limit?: number
  /**
   * @description Пагинация - сколько необходимо пропустить элементов
   * @type integer | undefined, int32
   */
  Offset?: number
}

type CreateQueryModelResult<FilterParams, Result, ResultItem> = {
  // Availability
  $isAvailable: StoreWritable<boolean>
  availableSet: EventCallable<void>
  unavailableSet: EventCallable<void>
  //   Filters
  $filters: StoreWritable<FilterParams>
  filtersInit: EventCallable<Partial<FilterParams>>
  filtersChanged: EventCallable<Partial<FilterParams>>
  filtersReset: EventCallable<void>
  //   Search
  $search: StoreWritable<string | null>
  searchChanged: EventCallable<string>
  searchReset: EventCallable<void>
  //   Params
  $params: Store<{
    params: FilterParams
  }>
  // Query
  query: Query<
    {
      params?: FilterParams
    },
    Result,
    unknown
  >
  refreshed: EventCallable<void>
  // Accumulated
  $accumulatedData: StoreWritable<ResultItem[]>
}

type CreateQueryModelWithPagination<
  FilterParams extends BaseQueryModelParams,
  Result,
  ResultItem,
> = CreateQueryModelResult<FilterParams, Result, ResultItem> & {
  // Pagination
  $page: StoreWritable<number>
  pageChanged: EventCallable<number>
  pageReset: EventCallable<void>
  $pageSize: StoreWritable<number>
  pageSizeChanged: EventCallable<number>
}

type CreateQueryModelParams<FilterParams, Result, ResultItem> = {
  initialFilters?: FilterParams
  handler: ({ params }: { params?: FilterParams }) => Promise<Result>
  pagination?: boolean
  accumulate?: (prevStore: ResultItem[], response: Result) => ResultItem[]
}

// Перегрузка для pagination: true
export function createQueryModel<
  FilterParams extends BaseQueryModelParams,
  Result,
  ResultItem,
>(
  params: CreateQueryModelParams<FilterParams, Result, ResultItem> & {
    pagination: true
  },
): CreateQueryModelWithPagination<FilterParams, Result, ResultItem>

// Перегрузка для pagination: false или не указан
export function createQueryModel<
  FilterParams extends BaseQueryModelParams,
  Result,
  ResultItem,
>(
  params: CreateQueryModelParams<FilterParams, Result, ResultItem> & {
    pagination?: false
  },
): CreateQueryModelResult<FilterParams, Result, ResultItem>

// Реализация
export function createQueryModel<
  FilterParams extends BaseQueryModelParams,
  Result,
  ResultItem,
>({
  initialFilters = {} as FilterParams,
  handler,
  pagination = false,
  accumulate,
}: CreateQueryModelParams<FilterParams, Result, ResultItem>) {
  // Availability
  const availableSet = createEvent()
  const unavailableSet = createEvent()
  const $isAvailable = createStore(false)
    .on(availableSet, () => true)
    .on(unavailableSet, () => false)

  // Filters
  const filtersInit = createEvent<Partial<FilterParams>>()
  const filtersChanged = createEvent<Partial<FilterParams>>()
  const filtersReset = createEvent()
  const $filters = createStore(initialFilters)
    .on(filtersInit, (_, payload) => ({ ...initialFilters, ...payload }))
    .on(filtersChanged, (state, payload) => ({ ...state, ...payload }))
    .reset(filtersReset)

  // Search
  const searchChanged = createEvent<string>()
  const searchReset = createEvent()
  const $search = createStore<string | null>(null)

  // Pagination
  const $page = createStore<number>(DEFAULT_PAGE)
  const pageChanged = createEvent<number>()
  const pageReset = createEvent()
  $page
    .on(pageChanged, (_, page) => Math.max(DEFAULT_PAGE, page))
    .on(pageReset, () => DEFAULT_PAGE)

  const $pageSize = createStore<number>(DEFAULT_PAGE_SIZE)
  const pageSizeChanged = createEvent<number>()
  $pageSize.on(pageSizeChanged, (_, size) => size)

  sample({
    clock: [$filters, $search],
    target: pageReset,
  })

  // Query params
  const $params = combine(
    $filters,
    $search,
    $page,
    $pageSize,
    (filters, search, page, pageSize) => {
      // Filters
      const params = { ...filters }
      if (search) params.SearchQuery = search.trim()
      // Pagination
      if (pagination) {
        params.Limit = pageSize
        params.Offset = (page - 1) * pageSize
      }
      return { params }
    },
  )

  // Query
  const query = createQuery({ handler })

  // Accumulation
  const initialAccumulatedData: ResultItem[] = []
  const accumulatedDataReset = createEvent()
  const $accumulatedData = createStore(initialAccumulatedData)
    .on(query.finished.success, (state, { result }) => {
      if (!accumulate) return state
      return accumulate(state, result)
    })
    .reset(accumulatedDataReset)

  // Debounce
  const paramsDebounced = debounce({
    source: $params,
    timeout: DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  })

  const searchChangedDebounced = debounce({
    source: searchChanged,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  const searchResetDebounced = debounce({
    source: searchReset,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  // Start query
  sample({
    clock: paramsDebounced,
    filter: $isAvailable,
    target: query.start,
  })

  //   Refresh
  const refreshed = createEvent()
  sample({
    source: $params,
    clock: refreshed,
    filter: $isAvailable,
    target: query.start,
  })

  // Change search

  $search
    .on(searchChangedDebounced, (_, search) => search)
    .reset(searchResetDebounced)

  // Reset logic
  sample({
    clock: unavailableSet,
    target: query.reset,
  })

  sample({
    clock: query.reset,
    target: [filtersReset, searchReset, pageReset],
  })

  sample({
    clock: pageReset,
    target: accumulatedDataReset,
  })

  const model: CreateQueryModelResult<FilterParams, Result, ResultItem> = {
    // Availability
    $isAvailable,
    availableSet,
    unavailableSet,
    // Filters
    $filters,
    filtersInit,
    filtersChanged,
    filtersReset,
    // Search
    $search,
    searchChanged,
    searchReset,
    // Params
    $params,
    // Query
    query,
    refreshed,
    // Accumulation
    $accumulatedData,
  }

  if (!pagination) return model

  const modelWithPagination: CreateQueryModelWithPagination<
    FilterParams,
    Result,
    ResultItem
  > = {
    ...model,
    // Pagination
    $page,
    pageChanged,
    pageReset,
    $pageSize,
    pageSizeChanged,
  }

  return modelWithPagination
}
